import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableChildProvided,
  Droppable,
  DroppableChildProvided
} from '../../../../../../lib/components/drag-n-drop';
import {ReactNodeGenerator} from '../../../../../../lib/helpers';
import {X9ContentStateStore} from '../../../common/X9ContentStateStore';
import {GapFillingModel, GapFillingParagraph, GapItem, TextItem} from '../../models';
import {DndGapFillingStore} from '../DndGapFillingStore';
import {DndGapState, ItemPosition, positionToItemId} from '../models';

interface Props {
  id: number,
  model: GapFillingModel,
  store: X9ContentStateStore,
  lessonType?: string
}

function renderGapFiller(item: GapItem, provided: DraggableChildProvided): React.ReactNode {
  const classname = classNames('gap-filling-dnd', 'gap-filler',
    {
      mirror: provided.isMirror,
      dragging: !provided.isMirror && provided.isDragging,
      default: !provided.isMirror && !provided.isDragging
    });
  return (
    <div className={classname}
         {...provided.draggableProps}
         {...provided.mirrorProps}>
      {item.value}
    </div>
  );
}

function gapFiller(itemId: string,
                   item: GapItem): React.ReactNode {
  return (
    <Draggable draggableId={itemId}>
      {
        (provided: DraggableChildProvided) =>
          renderGapFiller(item, provided)
      }
    </Draggable>);
}

function* gapFillers(store: DndGapFillingStore, provided: DroppableChildProvided): ReactNodeGenerator {
  const state = store.gapFillingState!;
  const usedGapItemIds = store.usedGapItemIds;

  for (let itemId of state.itemOrder) {
    const item = store.gapItemsById[itemId];
    if (item?.type !== 'gap')
      continue;

    const isUsed = usedGapItemIds.indexOf(itemId) !== -1;
    if (isUsed)
      yield(
        <div key={itemId} className="gap-filler-container">
          <div className="gap-filling-dnd gap-filler used">
            {item.value}
          </div>
        </div>
      );
    else
      yield (
        <div key={itemId} className="gap-filler-container">
          {gapFiller(itemId, item)}
        </div>
      );
  }
}

function textItem(item: TextItem, index: number) {
  return (<span key={index} className="text">{item.content}</span>);
}

function renderDroppableGapItem(itemId: string,
                                item: GapItem,
                                state: DndGapState,
                                filledWithItem: GapItem | null,
                                provided: DroppableChildProvided,): React.ReactNode {
  const isFilled = filledWithItem != null;
  const invalid = state.checked && state.filledWith !== null && state.filledWith !== itemId;


  const classname = classNames('gap-target', {
    filled: isFilled,
    empty: !isFilled,
    invalid: invalid,
  });

  return (
    <div className={classname} {...provided.droppableProps}>
      {
        filledWithItem !== null && state.filledWith !== null &&
        gapFiller(state.filledWith, filledWithItem)
      }
      {
        filledWithItem === null &&
        <div className="gap-filler-placeholder"><span>&nbsp;</span></div>
      }
    </div>);
}

function gapItem(item: GapItem,
                 store: DndGapFillingStore,
                 paragraph: number,
                 index: number): React.ReactNode {
  const position: ItemPosition = {paragraph: paragraph, index: index};
  const itemId = positionToItemId(position);

  let state = store.gapStates[itemId];
  // if (state === null || state === undefined)
  //   return null;

  const isCheckedValid = state.checked && state.filledWith !== null && itemId === state.filledWith;

  if (isCheckedValid)
    return (
      <div key={index} className="gap-target filled valid">
        <div className="gap-filling-dnd gap-filler">
          {item.value}
        </div>
      </div>
    );

  const filledWithItem = state.filledWith === null
    ? null
    : store.gapItemsById[state.filledWith];


  return (
    <Droppable key={index} droppableId={itemId}>
      {
        (provided: DroppableChildProvided) =>
          renderDroppableGapItem(itemId, item, state, filledWithItem, provided)
      }
    </Droppable>
  );
}

function* paragraphItems(store: DndGapFillingStore,
                         paragraphIndex: number,
                         paragraph: GapFillingParagraph): ReactNodeGenerator {
  for (let index = 0; index < paragraph.items.length; index++) {
    let item = paragraph.items[index];
    if (item.type === 'text')
      yield textItem(item, index);
    if (item.type === 'gap')
      yield gapItem(item, store, paragraphIndex, index);
  }
}

function* renderer(store: DndGapFillingStore): ReactNodeGenerator {
  const paragraphs = store.model.paragraphs;

  for (let index = 0; index < paragraphs.length; index++) {
    let paragraph = paragraphs[index];

    yield (
      <div key={index} className="gap-filling-paragraph">
        {
          Array.from(paragraphItems(store, index, paragraph))
        }
      </div>);
  }
}

@observer
export class StudentDndGapFilling extends React.Component<Props> {
  readonly store: DndGapFillingStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new DndGapFillingStore(props.id, props.model, props.store);
  }

  renderGapFillers = (provided: DroppableChildProvided) => {
    return (
      <div className="gap-fillers" {...provided.droppableProps}>
        {Array.from(gapFillers(this.store, provided))}
      </div>
    );
  };

  render() {
    const store = this.store;
    return (
      <>
        <DragDropContext canDropOn={store.canDropOn}
                         onDragUpdate={store.onDragUpdate}
                         onDragEnd={store.onDragEnd}>
          <div className="row">
            <div className="col">
              <div className="gap-filling-dnd">
                <Droppable droppableId="gap-fillers">
                  {this.renderGapFillers}
                </Droppable>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="gap-filling-dnd">
                {Array.from(renderer(this.store))}
              </div>
            </div>
          </div>

        </DragDropContext>
        {this.props.lessonType !== 'Examination' && (
          <div className="row justify-content-end">
            <div className="col-auto">
              <button type="button" className="btn btn-primary"
                      onClick={this.store.check}>
                Check
              </button>
            </div>
          </div>
        )}
      </>
    );
  }
}
